import { defineStore, acceptHMRUpdate } from 'pinia';
import i18n from '@/js/plugins/i18n';
import useUiMsgboxStore from '@/stores/ui/msgbox';
import useMultiprojectsStore from '@/stores/multiprojects/multiprojects';

function getInitialState() {
  return {
    openedElement: null,
    openedColorPickerElement: null,
    toasts: [],
  };
}

const invariantFields = ['id', 'config', 'dependencies'];

const store = defineStore('ui-dashboards', {
  state: () => getInitialState(),

  actions: {
    reset(values) {
      _.extend(this, getInitialState(), values);
    },
    openElement(el = null) {
      this.openedElement = el;
    },
    openColorPickerElement(el = null) {
      this.openedColorPickerElement = el;
    },
    openElementDetails(el) {
      const originalPlanningEl = useMultiprojectsStore().getOriginalPlanningEl(el);
      this.openElement(originalPlanningEl);
    },
    closeElementDetails(reason) {
      if (! this.openedElement) return;
      if (reason == 'delete') {
        this.deleteElement(this.openedElement.dashboard_el);
      } else {
        this.saveEl({ el: this.openedElement.dashboard_el, newState: this.openedElement.getAll() });
        if (reason == 'select-color') {
          this.openColorPicker(this.openedElement.dashboard_el);
        }
      }
      this.openElement(null);
    },
    openColorPicker(el) {
      const originalPlanningEl = useMultiprojectsStore().getOriginalPlanningEl(el);
      this.openColorPickerElement(originalPlanningEl);
    },
    closeColorPicker() {
      if (! this.openedColorPickerElement) return;
      this.saveEl({ el: this.openedColorPickerElement.dashboard_el, newState: this.openedColorPickerElement.getAll() });
      this.openColorPickerElement(null);
    },
    saveEl({ el, oldState: oldStateParam, newState: newStateParam, props = [] }) {
      const $t = i18n.global.t;
      const multiprojectsStore = useMultiprojectsStore();

      // el is dashboardEl
      // use oldState if el has been modified, or newState otherwise
      let oldState = oldStateParam;
      let newState = newStateParam;
      if (newState && ! oldState) {
        // apply new state
        newState = _.omit(newState, invariantFields);
        oldState = _.omit(el.getAll(), invariantFields);
        el.set(newState); // set rather than reset does not modify el if not needed (prevents reactive workloads/other_computed update)
      }
      newState = _.omit(el.getAll(), invariantFields);

      const originalPlanningEl = multiprojectsStore.getOriginalPlanningEl(el);
      if (! props.length) {
        let warningMsgBox;
        Object.keys(newState).forEach((prop) => {
          if (invariantFields.includes(prop)) return;
          if (angular.equals(newState[prop], oldState[prop])) return;
          if (['starttime', 'endtime'].includes(prop) && el.originalHasDependencies) {
            warningMsgBox = { title: $t('MONITORING_PROGRESS.ERROR_DATES'), body: $t('MONITORING_PROGRESS.ERROR_DATES_DESC') };
            el.setStartTime(oldState.starttime);
            el.setEndTime(oldState.endtime);
            return;
          }
          props.push(prop);
        });
        if (warningMsgBox) useUiMsgboxStore().open(warningMsgBox);
      }
      if (! props.length) return Promise.resolve('nothing to save');
      const originalPlanning = multiprojectsStore.getPlanningById(el.project_id);
      // if (! originalPlanning) originalPlanning = el.getPlanning(); // fallback when plannings are not all loaded (eg kanban in HomeUser)
      return originalPlanning.saveElement(originalPlanningEl, oldState, { props }).then((contentPatch) => {
        if (contentPatch) window.notificationsSrv.callEvent('projectSaved', { contentPatch, planning_id: originalPlanning.id, planning_title: originalPlanning.getTitle() });
      }).catch((message) => {
        el.set(oldState);
        if (message) useUiMsgboxStore().open({ title: $t('MONITORING_PROGRESS.ERROR_NOT_MODIFIED'), body: message || "" });
        return 'nothing to save';
      });
    },
    deleteElement(el) {
      const $t = i18n.global.t;
      const multiprojectsStore = useMultiprojectsStore();
      if (this.openedElement && this.openedElement.id == el.id) this.openElement(null);
      const originalPlanning = multiprojectsStore.getPlanningById(el.project_id);
      // if (! originalPlanning) originalPlanning = el.getPlanning(); // fallback when plannings are not all loaded (eg kanban in HomeUser)
      const originalPlanningEl = multiprojectsStore.getOriginalPlanningEl(el);
      originalPlanning.saveElement(originalPlanningEl, originalPlanningEl.getAll(), { forDeletion: true }).then((contentPatch) => {
        if (contentPatch) {
          const dashboardPlanning = el.getPlanning();
          if (dashboardPlanning) {
            const index = dashboardPlanning.elements.findIndex(element => element.id == el.id);
            if (index > -1) dashboardPlanning.elements.splice(index, 1);
          }
          window.notificationsSrv.callEvent('projectSaved', { contentPatch, planning_id: originalPlanning.id, planning_title: originalPlanning.getTitle() });
        }
      }).catch((message) => {
        if (message) useUiMsgboxStore().open({ title: $t('MONITORING_PROGRESS.ERROR_NOT_MODIFIED'), body: message || "" });
      });
    },
    showNoAcrToast() {
      const $t = i18n.global.t;
      this.toasts.push({
        color: 'warningorange',
        text: $t('MULTIPROJECTS.ERROR.CONSULT_ONLY_PROJECT'),
        icon: 'fas fa-exclamation-triangle',
        timeout: 3000,
      });
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
export default store;
